import { formatIban } from '@felix/domain';
import { Button, Loader, Modal, Tabs } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconBuildingBank,
  IconEye,
  IconEyeOff,
  IconId,
  IconRosetteDiscountCheck,
  IconStar,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { errorCode } from '../personnel/source.js';
import { PasswordDialog } from './PasswordDialog.js';
import classes from './SupplierProfile.module.css';
import { SupplierReviewsTab } from './SupplierReviewsTab.js';
import type {
  BankReviewAction,
  BankStatus,
  ProfileAddress,
  RevealedBankNumber,
  SupplierBankAccountView,
  SupplierProfileSource,
  SupplierProfileView,
} from './supplier-profile.js';

const STATUS_TONE: Record<BankStatus, StatusTone> = {
  pending: 'warning',
  verified: 'info',
  approved: 'success',
  rejected: 'danger',
  removed: 'neutral',
};

function useCountryName() {
  const { i18n } = useTranslation();
  return useMemo(() => {
    let names: Intl.DisplayNames | null = null;
    try {
      names = new Intl.DisplayNames([i18n.resolvedLanguage ?? 'en'], { type: 'region' });
    } catch {
      names = null;
    }
    return (code: string | null | undefined) => (code ? (names?.of(code) ?? code) : '—');
  }, [i18n.resolvedLanguage]);
}

function Address({ value }: { value: ProfileAddress }) {
  const country = useCountryName();
  return (
    <address className={classes.address}>
      {value.line1}
      {value.line2 ? (
        <>
          <br />
          {value.line2}
        </>
      ) : null}
      <br />
      {[value.postalCode, value.city, value.region].filter(Boolean).join(' ')}
      <br />
      {country(value.country)}
    </address>
  );
}

function ProfileTab({ view }: { view: SupplierProfileView }) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`supplierProfile.${key}`, options);
  const country = useCountryName();
  const details = view.details;
  if (!details) return <p className={classes.lead}>{k('noDetails')}</p>;
  return (
    <>
      <p className={classes.lead}>
        {k('readOnly')}
        {view.updatedAt ? ` ${k('updated', { date: formatDateTime(view.updatedAt) })}` : ''}
      </p>
      <div className={classes.columns}>
        <section className={classes.block} aria-label={k('legal.title')}>
          <h3>{k('legal.title')}</h3>
          <dl className={classes.facts}>
            <div>
              <dt>{k('legal.legalName')}</dt>
              <dd>{details.legalName}</dd>
            </div>
            <div>
              <dt>{k('legal.taxId')}</dt>
              <dd>{details.taxId}</dd>
            </div>
            <div>
              <dt>{k('legal.taxOffice')}</dt>
              <dd>{details.taxOffice ?? '—'}</dd>
            </div>
            <div>
              <dt>{k('legal.taxCountry')}</dt>
              <dd>{country(details.taxCountry)}</dd>
            </div>
            {view.portal?.accountName ? (
              <div>
                <dt>{k('legal.account')}</dt>
                <dd>
                  {view.portal.accountName}
                  {view.portal.accountEmail ? ` · ${view.portal.accountEmail}` : ''}
                </dd>
              </div>
            ) : null}
          </dl>
        </section>
        <section className={classes.block} aria-label={k('billing')}>
          <h3>{k('billing')}</h3>
          <Address value={details.billingAddress} />
          <h3 style={{ marginTop: 14 }}>{k('shipping')}</h3>
          {details.shippingAddress ? (
            <Address value={details.shippingAddress} />
          ) : (
            <p className={classes.address}>{k('sameAsBilling')}</p>
          )}
        </section>
      </div>
      <section className={classes.block} style={{ marginTop: 18 }} aria-label={k('contacts.title')}>
        <h3>{k('contacts.title')}</h3>
        <div className={classes.tableWrap}>
          <table className={classes.contacts}>
            <thead>
              <tr>
                <th scope="col">{k('contacts.name')}</th>
                <th scope="col">{k('contacts.role')}</th>
                <th scope="col">{k('contacts.email')}</th>
                <th scope="col">{k('contacts.phone')}</th>
              </tr>
            </thead>
            <tbody>
              {details.contacts.map((contact) => (
                <tr key={`${contact.email}-${contact.name}`}>
                  <td>{contact.name}</td>
                  <td>{contact.role}</td>
                  <td>{contact.email}</td>
                  <td>{contact.phone ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

type Review = { action: BankReviewAction; account: SupplierBankAccountView };

function AccountCard({
  supplierId,
  account,
  canReview,
  source,
  onReview,
}: {
  supplierId: string;
  account: SupplierBankAccountView;
  canReview: boolean;
  source: SupplierProfileSource;
  onReview: (review: Review) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`supplierProfile.bank.${key}`, options);
  const [revealed, setRevealed] = useState<RevealedBankNumber | null>(null);
  const [busy, setBusy] = useState(false);
  const current = account.current;
  const number = revealed
    ? revealed.iban
      ? formatIban(revealed.iban)
      : (revealed.accountNumber ?? '')
    : `${current.country ? `${current.country} ` : ''}${current.masked}`;
  const reveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    setBusy(true);
    try {
      setRevealed(await source.reveal(supplierId, current.versionId));
    } finally {
      setBusy(false);
    }
  };
  const open = account.status === 'pending' || account.status === 'verified';
  const effective = account.effective;
  return (
    <li className={classes.account} data-status={account.status}>
      <div className={classes.accountHead}>
        <span className={classes.bankName}>
          {current.bankName}
          <span className={classes.currency}>{current.currency}</span>
          {current.isDefault ? <StatusBadge tone="info">{k('default')}</StatusBadge> : null}
        </span>
        <StatusBadge tone={STATUS_TONE[account.status]}>
          {k(`status.${account.status}`)}
        </StatusBadge>
      </div>
      <div className={classes.number}>
        <span className={classes.digits}>{number}</span>
        {canReview && !current.removed ? (
          <Button
            size="compact-xs"
            variant="subtle"
            loading={busy}
            leftSection={revealed ? <IconEyeOff size={14} /> : <IconEye size={14} />}
            onClick={() => void reveal()}
          >
            {revealed ? k('hide') : k('show')}
          </Button>
        ) : null}
      </div>
      <dl className={classes.meta}>
        <div>
          <dt>{k('holder')}:</dt>
          <dd>{current.holderName}</dd>
        </div>
        {current.swiftBic ? (
          <div>
            <dt>{k('swift')}:</dt>
            <dd>{current.swiftBic}</dd>
          </div>
        ) : null}
      </dl>
      {canReview && !current.removed ? (
        <span className={classes.audited}>{k('audited')}</span>
      ) : null}
      {current.removed ? null : (
        <p className={classes.effective}>
          {account.status === 'approved'
            ? k('sameEffective')
            : effective
              ? k('effective', {
                  bank: effective.bankName,
                  number: `${effective.country ? `${effective.country} ` : ''}${effective.masked}`,
                })
              : k('noneEffective')}
        </p>
      )}
      {canReview && open ? (
        <div className={classes.actions}>
          {account.status === 'pending' ? (
            <Button size="xs" onClick={() => onReview({ action: 'verified', account })}>
              {k('verify')}
            </Button>
          ) : account.canApprove ? (
            <Button size="xs" onClick={() => onReview({ action: 'approved', account })}>
              {k('approve')}
            </Button>
          ) : (
            <span className={classes.audited}>{k('awaitingOther')}</span>
          )}
          <Button
            size="xs"
            variant="default"
            color="red"
            onClick={() => onReview({ action: 'rejected', account })}
          >
            {k('reject')}
          </Button>
        </div>
      ) : null}
      <ul className={classes.history} aria-label={k('history')}>
        {[
          ...account.versions.map((version) => ({
            at: version.createdAt,
            key: version.versionId,
            text: k(`change.${version.change}`, { n: version.versionNo }),
            note: null as string | null,
          })),
          ...account.reviews.map((review) => ({
            at: review.at,
            key: review.id,
            text: k(`review.${review.action}`, { name: review.userName ?? '—' }),
            note: review.note,
          })),
        ]
          .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
          .map((entry) => (
            <li key={entry.key}>
              <strong>{formatDateTime(entry.at)}</strong> · {entry.text}
              {entry.note ? <span className={classes.historyNote}>“{entry.note}”</span> : null}
            </li>
          ))}
      </ul>
    </li>
  );
}

export interface SupplierProfileDialogProps {
  supplierId: string;
  supplierName: string;
  source: SupplierProfileSource;
  onClose: () => void;
  /** Bildirim (başarı) metni; zil ve liste yenilensin diye. */
  onChanged?: (message: string) => void;
}

/**
 * Tedarikçi kartında şirket profili ve banka hesapları (ADR 0114 §6): profil salt okunur; banka
 * hesabı maskeli, tam numara yetkiyle ve denetimle açılır; dört göz: doğrulama aramasının kaydı,
 * farklı kişinin onayı ya da gerekçeli ret. Onaylanana kadar önceki onaylı hesap ödemede geçerlidir.
 */
export function SupplierProfileDialog({
  supplierId,
  supplierName,
  source,
  onClose,
  onChanged,
}: SupplierProfileDialogProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`supplierProfile.${key}`, options);
  const narrow = useMediaQuery('(max-width: 767px)') ?? false;
  const [view, setView] = useState<SupplierProfileView | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [tab, setTab] = useState<string | null>('profile');

  const load = useCallback(async () => {
    try {
      const next = await source.get(supplierId);
      setView(next);
      // Bekleyen banka değişikliği varsa doğrudan banka sekmesi
      if (next.bank?.accounts.some((a) => a.status === 'pending' || a.status === 'verified')) {
        setTab((current) => (current === 'profile' ? 'bank' : current));
      }
    } catch (error) {
      setFailed(errorCode(error));
    }
  }, [source, supplierId]);
  useEffect(() => {
    void load();
  }, [load]);

  const pending =
    view?.bank?.accounts.filter((a) => a.status === 'pending' || a.status === 'verified').length ??
    0;

  const confirm = async (input: { reason: string; password?: string }) => {
    if (!review) return null;
    try {
      const next = await source.review(supplierId, review.account.reviewVersionId, {
        action: review.action,
        detailsHash: review.account.detailsHash,
        ...(input.reason ? { note: input.reason } : {}),
        ...(input.password ? { password: input.password } : {}),
      });
      setView(next);
      onChanged?.(k(`notices.${review.action}`));
      setReview(null);
      return null;
    } catch (error) {
      const code = errorCode(error);
      if (code === 'STALE_VERSION' || code === 'CONTENT_CHANGED') void load();
      return code;
    }
  };

  return (
    <>
      <Modal
        opened={review === null}
        onClose={onClose}
        title={k('title', { name: supplierName })}
        size="xl"
        fullScreen={narrow}
        closeButtonProps={{ 'aria-label': k('close') }}
      >
        {failed ? (
          <p className={classes.lead} role="alert">
            {t(`purchasing.errors.${failed}`, { defaultValue: t('purchasing.errors.GENERIC') })}
          </p>
        ) : !view ? (
          <div style={{ display: 'grid', placeItems: 'center', minHeight: 160 }}>
            <Loader aria-label={k('loading')} />
          </div>
        ) : !view.portal ? (
          <p className={classes.lead}>{k('noPortal')}</p>
        ) : (
          <Tabs value={tab} onChange={setTab} keepMounted={false}>
            {view.portal.verified || view.portal.suspended ? (
              <div className={classes.badges}>
                {view.portal.verified ? (
                  <span title={k('verifiedHint')}>
                    <StatusBadge
                      tone="info"
                      icon={<IconRosetteDiscountCheck size={14} aria-hidden />}
                    >
                      {k('verified')}
                    </StatusBadge>
                  </span>
                ) : null}
                {view.portal.suspended ? (
                  <StatusBadge tone="danger">{k('suspended')}</StatusBadge>
                ) : null}
              </div>
            ) : null}
            <Tabs.List mb="md">
              <Tabs.Tab value="profile" leftSection={<IconId size={16} />}>
                {k('tabs.profile')}
              </Tabs.Tab>
              <Tabs.Tab
                value="bank"
                leftSection={<IconBuildingBank size={16} />}
                rightSection={
                  pending > 0 ? <StatusBadge tone="warning">{String(pending)}</StatusBadge> : null
                }
              >
                {k('tabs.bank')}
              </Tabs.Tab>
              {source.reviews ? (
                <Tabs.Tab value="reviews" leftSection={<IconStar size={16} />}>
                  {k('reviews.tab')}
                </Tabs.Tab>
              ) : null}
            </Tabs.List>
            <Tabs.Panel value="profile">
              <ProfileTab view={view} />
            </Tabs.Panel>
            <Tabs.Panel value="bank">
              {!view.bank ? (
                <p className={classes.lead}>{k('bank.prospect')}</p>
              ) : view.bank.accounts.length === 0 ? (
                <p className={classes.lead}>{k('bank.empty')}</p>
              ) : (
                <ul className={classes.accounts}>
                  {view.bank.accounts.map((account) => (
                    <AccountCard
                      key={account.bankAccountId}
                      supplierId={supplierId}
                      account={account}
                      canReview={view.can.review}
                      source={source}
                      onReview={setReview}
                    />
                  ))}
                </ul>
              )}
            </Tabs.Panel>
            {source.reviews ? (
              <Tabs.Panel value="reviews">
                <SupplierReviewsTab
                  supplierId={supplierId}
                  supplierName={supplierName}
                  source={source}
                  {...(onChanged ? { onNotice: onChanged } : {})}
                />
              </Tabs.Panel>
            ) : null}
          </Tabs>
        )}
      </Modal>
      {review ? (
        <PasswordDialog
          opened
          title={k(
            `dialogs.${review.action === 'verified' ? 'verify' : review.action === 'approved' ? 'approve' : 'reject'}Title`,
          )}
          body={k(
            `dialogs.${review.action === 'verified' ? 'verify' : review.action === 'approved' ? 'approve' : 'reject'}Body`,
          )}
          reason={review.action === 'approved' ? 'none' : 'required'}
          reasonLabel={
            review.action === 'verified' ? k('dialogs.verifyNote') : k('dialogs.rejectNote')
          }
          password
          reauthKind="supplier_bank_review"
          confirmLabel={k(
            `dialogs.${review.action === 'verified' ? 'verify' : review.action === 'approved' ? 'approve' : 'reject'}Confirm`,
          )}
          errorScope="bank"
          onClose={() => setReview(null)}
          onConfirm={confirm}
        />
      ) : null}
    </>
  );
}
